import type { BrandFilterStateSet, BrandUuid } from '../product/filter/state/brand';
import type { CheckboxFilterStateMap, CheckboxFilterValue } from '../product/filter/state/checkbox';
import { CheckboxFilterValue as CheckboxFilterValueSchema } from '../product/filter/state/checkbox';
import type { RangeFilterStateMap, RangeFilterValue } from '../product/filter/state/range';
import {
  makeContinuousRangeValue,
  makePriceRangeValue,
  makeSteppedRangeValue,
  RangeFilterValue as RangeFilterValueSchema,
} from '../product/filter/state/range';
import type { TextFilterStateMap } from '../product/filter/state/text';
import { BrandSerializable } from '../product/filter/stateToUrl/brandToUrl';
import { CheckboxToUrl } from '../product/filter/stateToUrl/checkboxToUrl';
import { RangeToUrl } from '../product/filter/stateToUrl/rangeToUrl';
import { TextToUrl } from '../product/filter/stateToUrl/textToUrl';
import {
  isCheckboxParam,
  isRangeParam,
  isTextParam,
} from '../product/filter/stateToUrl/urlPrefixes';
import {
  BigDecimal,
  Chunk,
  Effect,
  HashMap,
  HashSet as HS,
  Match,
  Option,
  pipe,
  Schema,
  SchemaGetter,
} from 'effect';

import { itemsPerPageParamName, pageParamName } from '../config/urlParams';

import type { TaxonCode, TaxonFilterStateSet } from '../product/filter/state/taxon';
import type { WarehouseCode, WarehouseFilterStateSet } from '../product/filter/state/warehouse';
import { TaxonSerializable } from '../product/filter/stateToUrl/taxonToUrl';
import { WarehouseSerializable } from '../product/filter/stateToUrl/warehouseToUrl';

/**
 * Internal state type using HashMap (what components work with)
 */
export const BRAND_URL_PARAM = 'brand';
export const WAREHOUSE_URL_PARAM = 'warehouseStock';
export const TAXON_URL_PARAM = 'taxon';

export type CompleteInternalState = {
  readonly checkboxFilters: CheckboxFilterStateMap;
  readonly textFilters: TextFilterStateMap;
  readonly rangeFilters: RangeFilterStateMap;
  readonly brands: BrandFilterStateSet;
  readonly warehouses: WarehouseFilterStateSet;
  readonly taxons: TaxonFilterStateSet;
  readonly page: Option.Option<number>;
  readonly itemsPerPage: Option.Option<number>;
};

/**
 * URL state type - this is the flattened Record format
 */
export type CompleteUrlState = Record<string, string>;

/**
 * For string-based URL parsing (SSR) - simplified approach
 */
export const parseUrlString = (urlString: string): CompleteUrlState => {
  const params = new URLSearchParams(urlString);
  const result: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    result[key] = value;
  }

  return result;
};

/**
 * Helper to convert internal HashMap state to URL state (flattened Record)
 * Combines all filter types and pagination into a single URL record
 */
export const internalStateToUrlState = (internal: CompleteInternalState): CompleteUrlState => {
  const result: Record<string, string> = {};

  // Convert checkbox filters to URL params
  const checkboxRecord = Schema.decodeSync(CheckboxToUrl)(internal.checkboxFilters);
  Object.assign(result, checkboxRecord);

  // Convert text filters to URL params
  const textRecord = Schema.decodeSync(TextToUrl)(internal.textFilters);
  Object.assign(result, textRecord);

  // Convert range filters to URL params
  const rangeRecord = Schema.decodeSync(RangeToUrl)(internal.rangeFilters);
  Object.assign(result, rangeRecord);

  // Convert brands to URL param
  if (HS.size(internal.brands) > 0) {
    result[BRAND_URL_PARAM] = Schema.encodeSync(BrandSerializable)(internal.brands);
  }

  // Convert warehouses to URL param
  if (HS.size(internal.warehouses) > 0) {
    result[WAREHOUSE_URL_PARAM] = Schema.encodeSync(WarehouseSerializable)(internal.warehouses);
  }

  // Convert taxons to URL param
  if (HS.size(internal.taxons) > 0) {
    result[TAXON_URL_PARAM] = Schema.encodeSync(TaxonSerializable)(internal.taxons);
  }

  // Add pagination parameters
  if (Option.isSome(internal.page)) {
    result.page = String(internal.page.value);
  }

  if (Option.isSome(internal.itemsPerPage)) {
    result.itemsPerPage = String(internal.itemsPerPage.value);
  }

  return result;
};

/**
 * Helper to convert URL state (flattened Record) to internal HashMap state
 * Separates URL params back into different filter types and pagination
 */
export const urlStateToInternalState = (urlRecord: CompleteUrlState): CompleteInternalState => {
  const checkboxParams: Record<string, string> = {};
  const textParams: Record<string, string> = {};
  const rangeParams: Record<string, string> = {};
  let brandString: string | undefined;
  let warehouseString: string | undefined;
  let taxonString: string | undefined;
  let page: number | undefined;
  let itemsPerPage: number | undefined;

  for (const [key, value] of Object.entries(urlRecord)) {
    if (key === pageParamName) {
      page = Number(value) || undefined;
    } else if (key === itemsPerPageParamName) {
      itemsPerPage = Number(value) || undefined;
    } else if (key === BRAND_URL_PARAM) {
      brandString = value;
    } else if (key === WAREHOUSE_URL_PARAM) {
      warehouseString = value;
    } else if (key === TAXON_URL_PARAM) {
      taxonString = value;
    } else if (isCheckboxParam(key)) {
      checkboxParams[key] = value;
    } else if (isTextParam(key)) {
      textParams[key] = value;
    } else if (isRangeParam(key)) {
      rangeParams[key] = value;
    }
  }

  const brands: BrandFilterStateSet = brandString
    ? Schema.decodeSync(BrandSerializable)(brandString)
    : HS.empty<BrandUuid>();

  const warehouses: WarehouseFilterStateSet = warehouseString
    ? Schema.decodeSync(WarehouseSerializable)(warehouseString)
    : HS.empty<WarehouseCode>();

  const taxons: TaxonFilterStateSet = taxonString
    ? Schema.decodeSync(TaxonSerializable)(taxonString)
    : HS.empty<TaxonCode>();

  // Convert URL records back to HashMap using existing schemas
  return {
    checkboxFilters: Schema.encodeSync(CheckboxToUrl)(checkboxParams),
    textFilters: Schema.encodeSync(TextToUrl)(textParams),
    rangeFilters: Schema.encodeSync(RangeToUrl)(rangeParams),
    brands,
    warehouses,
    taxons,
    page: Option.fromNullishOr(page),
    itemsPerPage: Option.fromNullishOr(itemsPerPage),
  };
};

const SerializableContinuousRange = Schema.TaggedStruct('ContinuousRange', {
  min: Schema.Finite,
  max: Schema.Finite,
});

const SerializablePriceRange = Schema.TaggedStruct('PriceRange', {
  min: Schema.Finite,
  max: Schema.Finite,
});

const SerializableSteppedRange = Schema.TaggedStruct('SteppedRange', {
  min: Schema.Finite,
  max: Schema.Finite,
  steps: Schema.Array(Schema.Finite),
});

const SerializableRangeValue = Schema.Union([
  SerializableContinuousRange,
  SerializablePriceRange,
  SerializableSteppedRange,
]);

type SerializableRangeValue = Schema.Schema.Type<typeof SerializableRangeValue>;

/**
 * Helper functions to encode/decode each range type using existing schemas
 */
const decodeRangeValue = (range: RangeFilterValue): SerializableRangeValue =>
  Match.value(range).pipe(
    Match.tag('ContinuousRange', (v) =>
      SerializableContinuousRange.make({
        min: BigDecimal.toNumberUnsafe(v.min),
        max: BigDecimal.toNumberUnsafe(v.max),
      })
    ),
    Match.tag('PriceRange', (v) =>
      SerializablePriceRange.make({
        min: BigDecimal.toNumberUnsafe(v.min),
        max: BigDecimal.toNumberUnsafe(v.max),
      })
    ),
    Match.tag('SteppedRange', (v) =>
      SerializableSteppedRange.make({
        min: BigDecimal.toNumberUnsafe(v.min),
        max: BigDecimal.toNumberUnsafe(v.max),
        steps: pipe(
          v.steps,
          Chunk.map((bd) => BigDecimal.toNumberUnsafe(bd)),
          Chunk.toArray
        ),
      })
    ),
    Match.exhaustive
  );

const encodeRangeValue = (serialized: SerializableRangeValue): RangeFilterValue =>
  Match.value(serialized).pipe(
    Match.tag('ContinuousRange', (v) =>
      makeContinuousRangeValue({
        min: BigDecimal.fromNumberUnsafe(v.min),
        max: BigDecimal.fromNumberUnsafe(v.max),
      })
    ),
    Match.tag('PriceRange', (v) =>
      makePriceRangeValue({
        min: BigDecimal.fromNumberUnsafe(v.min),
        max: BigDecimal.fromNumberUnsafe(v.max),
      })
    ),
    Match.tag('SteppedRange', (v) =>
      makeSteppedRangeValue({
        min: BigDecimal.fromNumberUnsafe(v.min),
        max: BigDecimal.fromNumberUnsafe(v.max),
        steps: Chunk.fromIterable(v.steps.map((n) => BigDecimal.fromNumberUnsafe(n))),
      })
    ),
    Match.exhaustive
  );

const RangeValueSerializable = Schema.toType(RangeFilterValueSchema).pipe(
  Schema.decodeTo(SerializableRangeValue, {
    decode: SchemaGetter.transform(decodeRangeValue),
    encode: SchemaGetter.transform(encodeRangeValue),
  })
);

/**
 * Serializable version of CompleteInternalState for SSR
 * Uses plain objects instead of HashMap for client component compatibility
 */
export type SerializableInternalState = {
  readonly checkboxFilters: Record<string, CheckboxFilterValue>;
  readonly textFilters: Record<string, readonly string[]>;
  readonly rangeFilters: Record<string, SerializableRangeValue>;
  readonly brands: readonly string[];
  readonly warehouses: readonly string[];
  readonly taxons: readonly string[];
  readonly page: number | null;
  readonly itemsPerPage: number | null;
};

/**
 * Helper schemas for SerializableInternalState fields
 */
const CheckboxFiltersSchema = Schema.Record(Schema.String, CheckboxFilterValueSchema);

const TextFiltersSchema = Schema.Record(Schema.String, Schema.Array(Schema.String));

const RangeFiltersSchema = Schema.Record(Schema.String, SerializableRangeValue);

const BrandsSchema = Schema.Array(Schema.String);
const WarehousesSchema = Schema.Array(Schema.String);
// Optional on the encoded side so history.state persisted before the taxon
// dimension shipped still decodes (absent → `[]`), required after decode.
const TaxonsSchema = Schema.Array(Schema.String).pipe(
  Schema.withDecodingDefaultKey(Effect.succeed<readonly string[]>([]))
);

/**
 * Schema for SerializableInternalState validation
 */
export const SerializableInternalStateSchema = Schema.Struct({
  checkboxFilters: CheckboxFiltersSchema,
  textFilters: TextFiltersSchema,
  rangeFilters: RangeFiltersSchema,
  brands: BrandsSchema,
  warehouses: WarehousesSchema,
  taxons: TaxonsSchema,
  page: Schema.NullOr(Schema.Finite),
  itemsPerPage: Schema.NullOr(Schema.Finite),
});

/**
 * Convert internal state to serializable format for SSR
 */
export const serializeInternalState = (
  internal: CompleteInternalState
): SerializableInternalState => {
  const checkboxFilters: Record<string, CheckboxFilterValue> = {};
  const textFilters: Record<string, readonly string[]> = {};
  const rangeFilters: Record<string, SerializableRangeValue> = {};

  HashMap.forEach(internal.checkboxFilters, (value, key) => {
    checkboxFilters[key] = value;
  });

  HashMap.forEach(internal.textFilters, (value, key) => {
    textFilters[key] = Array.from(value);
  });

  HashMap.forEach(internal.rangeFilters, (value, key) => {
    rangeFilters[key] = Schema.decodeSync(RangeValueSerializable)(value);
  });

  return {
    checkboxFilters,
    textFilters,
    rangeFilters,
    brands: Schema.encodeSync(BrandSerializable)(internal.brands)
      .split(',')
      .filter((s) => s.length > 0),
    warehouses: Schema.encodeSync(WarehouseSerializable)(internal.warehouses)
      .split(',')
      .filter((s) => s.length > 0),
    taxons: Schema.encodeSync(TaxonSerializable)(internal.taxons)
      .split(',')
      .filter((s) => s.length > 0),
    page: Option.isSome(internal.page) ? internal.page.value : null,
    itemsPerPage: Option.isSome(internal.itemsPerPage) ? internal.itemsPerPage.value : null,
  };
};

/**
 * Convert serializable format back to internal HashMap state
 */
export const deserializeInternalState = (
  serialized: SerializableInternalState
): CompleteInternalState => {
  return {
    checkboxFilters: HashMap.fromIterable(Object.entries(serialized.checkboxFilters)),
    textFilters: HashMap.fromIterable(
      Object.entries(serialized.textFilters).map(([key, values]) => [key, HS.fromIterable(values)])
    ),
    rangeFilters: HashMap.fromIterable(
      Object.entries(serialized.rangeFilters).map(
        ([key, value]) => [key, Schema.encodeSync(RangeValueSerializable)(value)] as const
      )
    ),
    brands: Schema.decodeSync(BrandSerializable)(serialized.brands.join(',')),
    warehouses: Schema.decodeSync(WarehouseSerializable)(serialized.warehouses.join(',')),
    taxons: Schema.decodeSync(TaxonSerializable)(serialized.taxons.join(',')),
    page: Option.fromNullishOr(serialized.page),
    itemsPerPage: Option.fromNullishOr(serialized.itemsPerPage),
  };
};
